import {
  EvStation,
  LocalGasStation,
  ElectricCar,
  DirectionsCar,
  CheckCircle,
} from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";

import AccordianUtils from "../../../../../utils/accordian";
import { SalesAidsStyles } from "./SalesAidsStyles";
import AppButton from "../../../../base/AppButton.component";
import Theme from "../../../../../utils/Theme";
import {
  ADMIN_WARRANTY,
} from "../../../../../graphql/mutation/mutation";
import {
  buildWarrantySaveInput,
  createInitialWarrantyState,
  mergeAllWarrantyFromFetch,
} from "./warrantyConstants";
import GasWarranty from "./GasWarranty.component";
import HybridWarranty from "./HybridWarranty.component";
import EvWarranty from "./EvWarranty.component";
import DieselWarranty from "./DieselWarranty.component";

const WarrantyModule = ({ data, edit, refetch }) => {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [warranty, setWarranty] = useState(() =>
    createInitialWarrantyState(data?.dealership_id || "")
  );

  const [adminWarranty] = useMutation(ADMIN_WARRANTY);

  /** Map getDealerships response into form state (like artifacts — merge, don't reset). */
  const fetchWarranty = (source) => {
    if (!source?.dealership_id) return;

    const coverages = source.warrantyCoverages || [];
    const exclusions = source.warrantyExclusions || [];
    const dealershipId = String(source.dealership_id);

    setWarranty((prev) => {
      const hasWarrantyData = coverages.length > 0 || exclusions.length > 0;
      const sameDealership =
        prev?.dealership_id != null &&
        String(prev.dealership_id) === dealershipId;

      // Keep current fields when refetch has no warranty payload yet.
      if (!hasWarrantyData && sameDealership) return prev;

      const base =
        sameDealership && prev
          ? prev
          : createInitialWarrantyState(dealershipId);

      return mergeAllWarrantyFromFetch(base, coverages, exclusions);
    });
  };

  useEffect(() => {
    if (data) {
      fetchWarranty(data);
    }
  }, [data]);

  const handleSaveCoverage = async () => {
    if (!warranty?.dealership_id) {
      setErrorMessage("Dealership is required before saving warranty coverage.");
      return;
    }

    setSaving(true);
    setSuccess(false);
    setErrorMessage("");

    try {
      const warrantySaveInput = buildWarrantySaveInput(warranty);
      const result = await adminWarranty({ variables: { warrantySaveInput } });
      const response = result?.data?.adminWarranty;
      const firstError = response?.errors?.[0];

      if (firstError) {
        setErrorMessage(
          firstError.message || "Failed to save warranty coverage."
        );
        return;
      }

      if (response?.status === "success") {
        // Same as artifacts: keep current fields, then refetch dealership.
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          if (edit && refetch) refetch();
        }, 2000);
      }
    } catch (err) {
      console.error("adminWarranty save error", err);
      setErrorMessage("Failed to save warranty coverage.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        p: "16px 16px 16px 16px",
        boxSizing: "border-box",
      }}
    >
      <Box sx={SalesAidsStyles.artifactSentence}>
        <Typography variant={data ? "h6" : "body1"}>Warranty Module</Typography>
      </Box>

      <Box sx={{ ...SalesAidsStyles.accordiansBox, flex: 1, minHeight: 0 }}>
        <AccordianUtils
          index={0}
          expandedIndex={expandedIndex}
          setExpandedIndex={setExpandedIndex}
          Text={"GAS"}
          icon={<LocalGasStation sx={SalesAidsStyles.abcIcon} />}
          content={
            <GasWarranty
              warranty={warranty?.gas}
              setWarranty={setWarranty}
            />
          }
        />
        <AccordianUtils
          index={1}
          expandedIndex={expandedIndex}
          setExpandedIndex={setExpandedIndex}
          Text={"Hybrid"}
          icon={<ElectricCar sx={SalesAidsStyles.abcIcon} />}
          content={
            <HybridWarranty
              warranty={warranty?.hybrid}
              setWarranty={setWarranty}
            />
          }
        />
        <AccordianUtils
          index={2}
          expandedIndex={expandedIndex}
          setExpandedIndex={setExpandedIndex}
          Text={"EV"}
          icon={<EvStation sx={SalesAidsStyles.abcIcon} />}
          content={
            <EvWarranty
              warranty={warranty?.ev}
              setWarranty={setWarranty}
            />
          }
        />
        <AccordianUtils
          index={3}
          expandedIndex={expandedIndex}
          setExpandedIndex={setExpandedIndex}
          Text={"Diesel"}
          icon={<DirectionsCar sx={SalesAidsStyles.abcIcon} />}
          content={
            <DieselWarranty
              warranty={warranty?.diesel}
              setWarranty={setWarranty}
            />
          }
        />
      </Box>

      <Box
        sx={{
          pt: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
          width: "100%",
        }}
      >
        {errorMessage ? (
          <Typography variant="caption" color="error">
            {errorMessage}
          </Typography>
        ) : null}

        {success ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: "14px",
              width: 280,
            }}
          >
            <CheckCircle color={"primary"} />
            <span
              style={{
                color: Theme.palette.primary.main,
                marginLeft: "4px",
              }}
            >
              SUCCESS
            </span>
          </Box>
        ) : (
          <AppButton
            variant="contained"
            buttonText={edit ? "Update Warranty" : "Save Warranty"}
            clickHandler={handleSaveCoverage}
            loading={saving}
            disabled={!warranty?.dealership_id || saving}
            sx={{ width: 280 }}
          />
        )}
      </Box>
    </Box>
  );
};

export default WarrantyModule;